import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, CalendarClock, RefreshCcw, StopCircle, ExternalLink } from "lucide-react";
import { PageHeader } from "@/pages/Accounts";

const DAY_ABBR = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export default function ScheduledPage() {
  const { id: projectId } = useParams();
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/campaigns/scheduled`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const stop = async (cid) => {
    try {
      await api.post(`/campaigns/${cid}/stop-recurring`);
      toast.success("Recurring stopped");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8 space-y-6">
      <Link to={`/projects/${projectId}`} className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" /> Back to project
      </Link>
      <PageHeader
        title="Scheduled campaigns"
        description="Upcoming one-time sends and active recurring templates."
      />

      <Tabs defaultValue="recurring" data-testid="sched-tabs">
        <TabsList>
          <TabsTrigger value="recurring" data-testid="sched-tab-recurring">
            Active recurring ({data?.active_recurring?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="onetime" data-testid="sched-tab-onetime">
            Upcoming one-time ({data?.upcoming_one_time?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="recurring" className="mt-4">
          <Card data-testid="sched-recurring-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <RefreshCcw className="h-4 w-4" /> Recurring templates
              </CardTitle>
              <CardDescription>
                Templates that spawn a fresh campaign on schedule. Click&nbsp;view runs to see history.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Interval</TableHead>
                    <TableHead>Time (tz)</TableHead>
                    <TableHead>Next run (UTC)</TableHead>
                    <TableHead>Last run</TableHead>
                    <TableHead className="text-right">Runs</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!data?.active_recurring || data.active_recurring.length === 0) && (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-xs text-muted-foreground">No recurring campaigns yet.</TableCell></TableRow>
                  )}
                  {data?.active_recurring?.map(r => (
                    <TableRow key={r.id} data-testid={`sched-recur-${r.id}`}>
                      <TableCell className="text-sm">
                        <Link to={`/campaigns/${r.id}`} className="hover:underline">{r.name}</Link>
                      </TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="outline" className="text-[10px]">{r.interval}</Badge>
                        {r.interval === "weekly" && r.days_of_week && (
                          <span className="ml-2 font-mono text-[10px]">{r.days_of_week.map(d => DAY_ABBR[d]).join(",")}</span>
                        )}
                        {r.interval === "monthly" && r.day_of_month && (
                          <span className="ml-2 font-mono text-[10px]">day {r.day_of_month}</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.time_of_day} {r.tz?.split("/")[1] || r.tz}</TableCell>
                      <TableCell className="font-mono text-[11px]">{r.next_run_at?.replace("T"," ").slice(0,19) || "—"}</TableCell>
                      <TableCell className="font-mono text-[11px]">{r.last_run_at?.replace("T"," ").slice(0,19) || "—"}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{r.run_count || 0}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button asChild size="sm" variant="ghost">
                          <Link to={`/campaigns/${r.id}?tab=runs`} data-testid={`sched-view-runs-${r.id}`}><ExternalLink className="h-3 w-3 mr-1" />Runs</Link>
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="destructive" data-testid={`sched-stop-${r.id}`}><StopCircle className="h-3 w-3 mr-1" />Stop</Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Stop this recurring template?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Historical runs remain intact. No new clones will be spawned. You can re-enable by calling <span className="font-mono">make-recurring</span> again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => stop(r.id)} data-testid={`sched-stop-confirm-${r.id}`}>Stop recurring</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="onetime" className="mt-4">
          <Card data-testid="sched-onetime-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarClock className="h-4 w-4" /> Upcoming one-time sends
              </CardTitle>
              <CardDescription>Campaigns queued with a future <span className="font-mono">schedule_at</span>.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Scheduled at (UTC)</TableHead>
                    <TableHead className="text-right">Final recipients</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!data?.upcoming_one_time || data.upcoming_one_time.length === 0) && (
                    <TableRow><TableCell colSpan={3} className="text-center py-8 text-xs text-muted-foreground">Nothing scheduled.</TableCell></TableRow>
                  )}
                  {data?.upcoming_one_time?.map(r => (
                    <TableRow key={r.id} data-testid={`sched-onetime-${r.id}`}>
                      <TableCell className="text-sm">
                        <Link to={`/campaigns/${r.id}`} className="hover:underline">{r.name}</Link>
                      </TableCell>
                      <TableCell className="font-mono text-[11px]">{r.schedule_at?.replace("T"," ").slice(0,19) || "—"}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{new Intl.NumberFormat("en-IN").format(r.final_recipients || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
